<script lang="ts">
    import Text from '../Text.svelte';
    import Button from '../Button.svelte';
    import Input from './Input.svelte';
    import Box from '../_wrappers/Box.svelte';
    import { Field, Form } from 'svelte-final-form';
    import { composeValidation, getTranslatedValidationError, validationMap } from './validations';
    import { DEFAULT_LAYOUT } from '$lib/components/constants';
    import { getBoxAttributes } from '$lib/components/_DisplayComponents/_utils/getBoxAttributes';

    // Types
    import type { Align, Box as BoxType, Size } from '$lib/types/Components/SharedTypes';
    import type { RawDraftContentState } from 'draft-js';
    import type { FormValues, Input as InputType } from '$lib/types/Form';
    import { InputValidationType } from '$lib/types/Form';
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Language } from '$lib/types/Language';
    import { PhoneValidationKind } from '$lib/types/PhoneValidationKind';
    import type { ValidationError } from './validations';

    // Question
    export let align: Align = 'center';
    export let draft: RawDraftContentState;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let language: Language = 'de';
    export let layout = DEFAULT_LAYOUT;

    // Inputs
    export let inputs: InputData[] = [];
    export let fileUploading = false;

    // Submit Button
    export let btnId = '';
    export let btnDraft: RawDraftContentState;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAnimation = '';
    export let btnAlign: Align = 'center';
    export let btnSize: Size = 'medium';
    export let loading = false;
    export let disabled = false;

    const { isInColumn } = layout;

    const btnBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'none',
        left: 'none',
    };

    const textBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'large',
        left: 'none',
    };

    const generateInitialValues = (inputs: InputType[]) => {
        const values: Record<string, string> = {};

        inputs.forEach((input) => {
            values[input.name] = '';
        });

        return values;
    };

    const initialValues = generateInitialValues(inputs);

    const getValidation = ({ type, required, customOptions }: InputData) => {
        const typeToUse =
            type === 'phone' &&
            customOptions?.phoneValidationKind === PhoneValidationKind.CountryFormat
                ? InputValidationType.PHONE_V2
                : type;
        const withType = (
            fn: (value: string, type: InputValidationType) => ValidationError | undefined,
        ) => {
            return (value: string) => {
                return fn(value, typeToUse);
            };
        };
        const inputTypeValidator = validationMap[typeToUse]
            ? withType(validationMap[typeToUse])
            : undefined;

        if (required && inputTypeValidator) {
            return composeValidation(
                withType(validationMap[InputValidationType.REQUIRED]),
                inputTypeValidator,
            );
        }

        if (required && !inputTypeValidator) {
            return composeValidation(withType(validationMap[InputValidationType.REQUIRED]));
        }

        return inputTypeValidator;
    };
</script>

<Box
    {...getBoxAttributes({ box, isInColumn })}
    {transitionDelay}
    {transitionEffect}
    narrow={!isInColumn}
>
    <Text {transitionDelay} raw={draft} {align} {color} box={textBox} {layout} />
    <Form {onSubmit} {initialValues} let:form let:state>
        <form class="sm:mt-2" on:submit|preventDefault={form.submit}>
            {#each inputs as formInput, index}
                <Field name={formInput.name} let:input let:meta validate={getValidation(formInput)}>
                    <Input
                        finalFormInput={input}
                        isFirst={index === 0}
                        isLast={index + 1 === inputs.length}
                        invalid={meta.error && meta.touched}
                        pristine={meta.pristine}
                        touched={meta.touched}
                        active={meta.active}
                        uploading={fileUploading}
                        errorMessage={getTranslatedValidationError(meta.error, language)}
                        {...formInput}
                        {language}
                    />
                </Field>
            {/each}
            <div class="mt-4">
                <Button
                    id={btnId}
                    draft={btnDraft}
                    color={btnColor}
                    bgColor={btnBgColor}
                    buttonType="submit"
                    {onMouseEnter}
                    align={btnAlign}
                    size={btnSize}
                    box={btnBox}
                    animation={btnAnimation}
                    {loading}
                    disabled={disabled || state.invalid || state.submitting}
                />
            </div>
        </form>
    </Form>
</Box>
